// What the app may claim about the connection.
//
// The offline PAGE is what the browser shows when a navigation fails. This is
// the other half: what an already-open app says when requests start failing
// underneath someone who is still looking at a working-looking screen.
//
// ⚠️ THE ASYMMETRY THAT GOVERNS EVERYTHING HERE. `navigator.onLine === false`
// proves the device has no network. `true` proves NOTHING: it is true on a
// captive portal, on wifi with no route, and when our own servers are down.
// So "offline" may be asserted from the flag, and "working" never may — the
// only evidence that requests are getting through is a request that got
// through.
//
// The two states need different words because they need different actions:
// "you are offline" sends someone to their wifi, "we cannot reach KaziPeople"
// does not.

export type ConnectionState =
  /** Requests are getting through. */
  | 'online'
  /** The DEVICE has no network — navigator.onLine is false. */
  | 'offline'
  /** The device thinks it is online but our requests are failing. */
  | 'unreachable';

export interface ConnectionInput {
  /** navigator.onLine, read by the caller. */
  deviceOnline: boolean;
  /** Consecutive request failures with NO response at all (not 4xx/5xx —
   *  a server that answers is a server we reached). */
  consecutiveFailures: number;
}

/**
 * How many no-response failures before we say anything.
 *
 * ⚠️ Not 1. A single dropped request is ordinary — a sleeping laptop, a
 * handover between cells, one flaky connection. A banner for every blip is a
 * banner people learn to ignore, and this one has to be believed when it
 * matters.
 */
export const FAILURES_BEFORE_UNREACHABLE = 2;

export const connectionState = (i: ConnectionInput): ConnectionState => {
  // The flag's one reliable answer. Checked first: when the device is off the
  // network, failures are explained and the gentler wording is wrong.
  if (!i.deviceOnline) return 'offline';
  if (i.consecutiveFailures >= FAILURES_BEFORE_UNREACHABLE) return 'unreachable';
  return 'online';
};

export interface ConnectionMessage {
  title: string;
  detail: string;
}

/** What to say. null = say nothing, which is most of the time. */
export const connectionMessage = (s: ConnectionState): ConnectionMessage | null => {
  if (s === 'offline') {
    return {
      title: 'You are offline',
      detail:
        'Your work is still here and nothing has been lost. Anything you have typed stays on screen until the connection returns.',
    };
  }
  if (s === 'unreachable') {
    // ⚠️ Does NOT say "you are offline": their wifi may be perfectly fine and
    // sending them to it wastes their time. It also does not claim KaziPeople
    // is down — we know our requests are failing, not why.
    return {
      title: 'Cannot reach KaziPeople',
      detail:
        'Your connection looks fine, so this is on our side or in between. Your work is still here — nothing has been lost.',
    };
  }
  return null;
};

/**
 * Whether a request failure counts towards `consecutiveFailures`.
 *
 * ⚠️ ONLY a failure with no response. A 4xx or 5xx means we REACHED the
 * server: the request was answered and refused, which is a different problem
 * and must not raise a connection banner. Counting them would put "cannot
 * reach KaziPeople" on screen for a validation error.
 */
export const countsAsConnectionFailure = (status: number | undefined | null): boolean =>
  status === undefined || status === null || status === 0;
